// src/pages/auth/Login.jsx
import { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { getDoc, doc, setDoc, serverTimestamp } from "firebase/firestore";
import Logo from "../../images/2.png"
// ✅ عدّل المسار إذا كان firebaseConfig عندك بمكان مختلف
import { auth, db } from "../../../firebaseConfig";

/* ألوان مستخرجة من الصورة (Grayscale فقط) */
const BRAND = {
  ink: "#2F2F2F",
  graphite: "#878787",
  silver: "#B8B8B8",
  mist: "#D0D0D0",
  paper: "#EFEFEF",
  white: "#FCFCFC",
};

function normalizeLoginEmail(value) {
  const clean = value.trim();
  return clean.includes("@") ? clean : `${clean}@gmail.com`;
}

function buildDefaultAdminProfile(user) {
  const fallbackName = user.email?.split("@")[0] || "admin";
  const displayName = user.displayName || fallbackName;

  return {
    role: 1,
    email: user.email || "",
    displayName,
    name: displayName,
    storeName: displayName,
    phone: "",
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    defaultAdmin: true,
  };
}

function SketchContainerMark() {
  // SVG بسيط (خطوط فقط) بنفس روح الرسمة (بدون ألوان خارج الصورة)
  return (
    <img className="w-full h-auto"
      src={Logo} alt="" />
  );
}

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const ui = useMemo(() => {
    return {
      pageBg: `radial-gradient(1200px 520px at 90% -30%, ${BRAND.paper}, ${BRAND.white} 55%)`,
      cardBorder: `1px solid ${BRAND.paper}`,
      cardShadow:
        "0 18px 50px rgba(0,0,0,0.06), 0 6px 18px rgba(0,0,0,0.04)",
      headerBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
      btnBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
      ring: `0 0 0 4px rgba(135,135,135,0.18)`,
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const loginEmail = normalizeLoginEmail(email);
      const cred = await signInWithEmailAndPassword(
        auth,
        loginEmail,
        password
      );
      const user = cred.user;

      // توكن صالح 24 ساعة
      const token = await user.getIdToken(true);
      const expirationTime = Date.now() + 24 * 60 * 60 * 1000;

      // جلب الدور من users/{uid}، وإن لم توجد بيانات ننشئ حساب الأدمن الافتراضي مرة واحدة.
      let role = 2;
      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);
        
        if (snap.exists()) {
          const data = snap.data() || {};
          if (data.role !== undefined && data.role !== null) {
            role = Number(data.role);
            
          }
        } else {
          await setDoc(userRef, {
            ...buildDefaultAdminProfile(user),
            email: user.email || loginEmail,
          });
          role = 1;
        }
      } catch (profileErr) {
        console.error(profileErr);
        // لا نمنع الدخول لو فشل الجلب، لكن لا نعطي صلاحية أدمن بدون بيانات.
      }

      localStorage.setItem("token", token);
      localStorage.setItem("tokenExpiration", String(expirationTime));
      localStorage.setItem("supplierId", user.uid);
      localStorage.setItem("role", String(role));

      if (onLogin) onLogin(true);
      navigate(from, { replace: true });
    } catch (err) {
      if (err?.code === "auth/user-not-found") setError("البريد غير مسجّل.");
      else if (err?.code === "auth/wrong-password")
        setError("كلمة المرور غير صحيحة.");
      else if (err?.code === "auth/too-many-requests")
        setError("محاولات عديدة، جرّب لاحقًا.");
      else setError(err?.message || "حدث خطأ أثناء تسجيل الدخول.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      dir="rtl"
      className="min-h-screen p-4"
      style={{
        display: "grid",
        placeItems: "center",
        background: ui.pageBg,
      }}
    >
      <div className="w-full max-w-md">
        {/* بطاقة */}
        <div
          className="rounded-3xl overflow-hidden bg-white"
          style={{
            border: ui.cardBorder,
            boxShadow: ui.cardShadow,
            background: `linear-gradient(180deg, ${BRAND.white}, ${BRAND.white})`,
          }}
        >
          {/* هيدر */}
          <div
            className="px-6 pt-7 pb-5   "
            style={{
              background: ui.headerBg,
              borderBottom: `1px solid rgba(255,255,255,0.12)`,
            }}
          >

            <div className="mt-2 ">
              <SketchContainerMark />
            </div>
          </div>

          {/* جسم */}
          <form onSubmit={handleSubmit} className="px-6 py-6">
            {error && (
              <div
                className="mb-4 rounded-2xl px-4 py-3"
                style={{
                  background: BRAND.paper,
                  border: `1px solid ${BRAND.mist}`,
                  color: BRAND.ink,
                  fontFamily: "Tajawal, system-ui, sans-serif",
                  fontSize: 14,
                }}
              >
                {error}
              </div>
            )}

            <label
              className="block text-sm mb-2"
              style={{
                color: BRAND.ink,
                fontFamily: "Tajawal, system-ui, sans-serif",
                fontWeight: 800,
              }}
            >
              الاسم 
            </label>
            <input
              type="text"
              required
              value={email}
              autoComplete="username"
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-2xl border px-3 py-3 mb-4 outline-none"
              style={{
                borderColor: BRAND.mist,
                background: BRAND.white,
                color: BRAND.ink,
                fontFamily: "Tajawal, system-ui, sans-serif",
              }}
              onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
              onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
              placeholder="example"
            />

            <label
              className="block text-sm mb-2"
              style={{
                color: BRAND.ink,
                fontFamily: "Tajawal, system-ui, sans-serif",
                fontWeight: 800,
              }}
            >
              كلمة المرور
            </label>
            <input
              type="password"
              required
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border px-3 py-3 mb-5 outline-none"
              style={{
                borderColor: BRAND.mist,
                background: BRAND.white,
                color: BRAND.ink,
                fontFamily: "Tajawal, system-ui, sans-serif",
              }}
              onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
              onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
              placeholder="••••••••"
            />

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-2xl text-white font-extrabold transition disabled:opacity-60"
              style={{
                padding: "12px 14px",
                background: ui.btnBg,
                boxShadow: "0 10px 22px rgba(0,0,0,0.10)",
                fontFamily: "Tajawal, system-ui, sans-serif",
                letterSpacing: 0.2,
              }}
            >
              {submitting ? "جارٍ الدخول..." : "دخول"}
            </button>
          </form>

          {/* فوتر */}
          <div
            className="px-6 py-4 text-center text-xs"
            style={{
              background: BRAND.white,
              borderTop: `1px solid ${BRAND.paper}`,
              color: BRAND.graphite,
              fontFamily: "Tajawal, system-ui, sans-serif",
            }}
          >
            إذا واجهت مشكلة في تسجيل الدخول تواصل مع الدعم.
          </div>
        </div>

        {/* شريط سفلي خفيف */}
        <div
          className="mt-4 text-center text-xs"
          style={{
            color: BRAND.graphite,
            fontFamily: "Tajawal, system-ui, sans-serif",
          }}
        >
          © 2025 Alayadi Digital Solutions
        </div>
      </div>
    </div>
  );
}
